import { crsLabel, isLocal, normalizeCrs } from './georef';
import { formatNumber } from './layer-style';
import { describeSkipped, crsDisplayLabel } from './xyz-geojson';

/**
 * Pure helpers for the Info tab of the properties panel: field inference, geometry
 * summary, bbox and the layer's source / dataset description. No React.
 */

const SAMPLE_SCAN = 500;

/** Inferred type of one value: 'number' | 'boolean' | 'date' | 'text'. */
export function inferValueType(v) {
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return Number.isFinite(v) ? 'number' : 'text';
  if (typeof v === 'string') {
    const t = v.trim();
    if (t !== '' && Number.isFinite(Number(t))) return 'number';
    if (/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}.*)?$/.test(t) && !Number.isNaN(Date.parse(t))) return 'date';
    return 'text';
  }
  return 'text';
}

/**
 * Field list from the first 500 features: name, dominant type ('mixed' when no type holds
 * 80% of the values), how many of the scanned features carry a value, and up to `maxSamples`
 * distinct sample values.
 */
export function inferFields(features, maxSamples = 3) {
  const scan = (features || []).slice(0, SAMPLE_SCAN);
  const fields = new Map();
  scan.forEach((f) => {
    Object.entries(f?.properties || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      if (!fields.has(k)) fields.set(k, { name: k, types: {}, count: 0, samples: [] });
      const e = fields.get(k);
      const t = inferValueType(v);
      e.types[t] = (e.types[t] || 0) + 1;
      e.count++;
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (e.samples.length < maxSamples && !e.samples.includes(s)) e.samples.push(s);
    });
  });
  return [...fields.values()].map(({ name, types, count, samples }) => {
    const [top, n] = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
    return { name, type: n / count >= 0.8 ? top : 'mixed', count, scanned: scan.length, samples };
  });
}

/** { Point: 12, LineString: 3 } across the features. */
export function geometryCounts(features) {
  const out = {};
  (features || []).forEach((f) => {
    const t = f?.geometry?.type;
    if (t) out[t] = (out[t] || 0) + 1;
  });
  return out;
}

/** [minLon, minLat, maxLon, maxLat] over (up to `limit`) features, or null when empty. */
export function featuresBbox(features, limit = 50000) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const scan = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number') {
      if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
    } else c.forEach(scan);
  };
  (features || []).slice(0, limit).forEach((f) => scan(f?.geometry?.coordinates));
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

export const formatBbox = (b, d = 5) => (b ? b.map((v) => formatNumber(v, { decimals: d })).join(', ') : '');

/**
 * Where a layer came from: { kind: 'demo'|'file'|'analysis'|'dataset'|'service'|'raster'|'tiles', label, detail }.
 * `layer` is the descriptor from lib/properties-layers.js.
 */
export function describeSource(layer) {
  if (!layer) return null;
  switch (layer.origin) {
    case 'dataset': return { kind: 'dataset', label: 'Survey dataset', detail: layer.dataset?.id || layer.datasetId || '' };
    case 'raster': return { kind: 'raster', label: 'Raster dataset', detail: layer.datasetId || '' };
    case 'tiles': return { kind: 'tiles', label: 'Vector tiles', detail: layer.datasetId || '' };
    case 'service': return { kind: 'service', label: 'Map service', detail: layer.url || '' };
    case 'analysis': return { kind: 'analysis', label: 'Analysis result', detail: layer.resultKind || '' };
    case 'file': return { kind: 'file', label: 'Imported file', detail: layer.fileName || layer.name || '' };
    default: return { kind: 'demo', label: 'Project data', detail: '' };
  }
}

/**
 * Rows for an XYZ (CSV/TXT) import from its `metadata.xyz`: source CRS, rows read, points
 * plotted and skipped rows with the reasons and first row numbers (V.46). [] for other layers.
 */
export function importSummary(xyz) {
  if (!xyz) return [];
  const rows = [];
  if (xyz.crs) rows.push(['Source CRS', `${crsDisplayLabel(xyz.crs)}${String(xyz.crs).startsWith('EPSG:') ? ` (${xyz.crs})` : ''}`]);
  if (xyz.mapping?.x && xyz.mapping?.y) rows.push(['Columns', `X = ${xyz.mapping.x}, Y = ${xyz.mapping.y}${xyz.zFields?.length ? `, Z = ${xyz.zFields.join(', ')}` : ''}`]);
  const skipped = xyz.skippedRows || 0;
  if (Number.isFinite(xyz.totalRows)) {
    rows.push(['Rows read', Number(xyz.totalRows).toLocaleString()]);
    rows.push(['Points plotted', Number(xyz.totalRows - skipped).toLocaleString()]);
  }
  rows.push(['Skipped rows', skipped ? describeSkipped(xyz).replace(/^\d+ rows? skipped: ?/, `${skipped.toLocaleString()}: `) : 'none']);
  return rows;
}

/** CRS and georef summary rows for a layer; vector layers on the map are always WGS84. */
export function crsSummary(layer, georef = null) {
  const rows = [];
  if (georef && !isLocal(georef)) {
    rows.push(['CRS', `${crsLabel(georef.crs)} (${normalizeCrs(georef.crs)})`]);
    rows.push(['Georeferenced', georef.mode ? `yes, ${String(georef.mode).replace(/_/g, ' ')}` : 'yes']);
    if (Number.isFinite(georef.elevation_offset) && georef.elevation_offset) rows.push(['Elevation offset', `${georef.elevation_offset} m`]);
  } else if (georef) {
    rows.push(['CRS', 'Local survey coordinates']);
    rows.push(['Georeferenced', 'no']);
  } else {
    rows.push(['CRS', 'WGS 84 (EPSG:4326)']);
  }
  return rows;
}

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : `${d.toISOString().replace('T', ' ').slice(0, 16)} UTC`;
};

/**
 * Metadata summary of the dataset behind a layer (the /api/datasets/:id record), as
 * { rows: [[label, value]], links: [{ to, label }], georef }. Covers raw survey, QC,
 * raster and tile metadata; `links` point at the dataset detail page where the survey,
 * QC, raster and vector-tile cards live.
 */
export function datasetSummary(ds) {
  if (!ds) return { rows: [], links: [], georef: null };
  const m = ds.metadata || {};
  const rows = [];
  const add = (k, v) => { if (v !== undefined && v !== null && v !== '') rows.push([k, String(v)]); };
  add('Dataset', ds.name);
  add('Type', ds.dataset_type || ds.method || m.format);
  add('Status', ds.status);
  add('QC status', ds.qc_status);
  // Raw survey
  add('Instrument', m.instrument);
  add('Array', m.array_type);
  add('Electrodes', m.n_electrodes);
  add('Readings', m.n_readings);
  add('Spacing', m.spacing !== undefined && m.spacing !== null ? `${m.spacing}${m.units ? ` ${m.units}` : ''}` : '');
  if (m.has_ip !== undefined) add('IP', m.has_ip ? `yes${m.n_ip_windows ? `, ${m.n_ip_windows} windows` : ''}` : 'no');
  if (Array.isArray(m.warnings) && m.warnings.length) add('Parser warnings', m.warnings.length);
  // Raster and tiles
  const r = m.raster;
  if (r) {
    add('Raster', r.kind === 'dem' ? 'DEM' : 'raster');
    add('Raster size', r.width && r.height ? `${r.width} x ${r.height} px, ${r.count || '?'} band(s)` : '');
    add('Raster CRS', r.crs_name ? `${r.crs} (${r.crs_name})` : r.crs);
  }
  if (m.tiles) {
    add('Vector tiles', m.tiles.ready ? `ready (${Object.keys(m.tiles.kinds || {}).join(', ') || 'no kinds'})` : 'not built');
    add('Tiled features', m.feature_count);
  } else add('Features', m.feature_count);
  add('Created', fmtDate(ds.created_at));
  add('Last modified', fmtDate(ds.updated_at));
  const links = ds.id ? [{ to: `/datasets/${ds.id}`, label: r ? 'Open raster card' : m.tiles ? 'Open vector tiles card' : 'Open survey / QC card' }] : [];
  return { rows, links, georef: m.georef || null };
}
